<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="close">
      <div class="howto" role="dialog" aria-label="How to play">
        <button class="howto__close" aria-label="Close" @click="close">✕</button>

        <div class="howto__body">
          <h3>{{ slide.title }}</h3>

          <div v-if="slide.cards" class="howto__cards">
            <img
              v-for="id in slide.cards"
              :key="id"
              :src="`${base}card-art/${id}.jpg`"
              :alt="altFor(id)"
              class="howto__card"
            >
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -- content is this file's own
               constant strings below, never user input -->
          <p v-for="(line, i) in slide.lines" :key="i" class="howto__line" v-html="line" />
        </div>

        <div class="howto__nav">
          <button class="secondary" :disabled="index === 0" @click="index -= 1">Back</button>
          <span class="howto__dots">
            <i
              v-for="(s, i) in slides"
              :key="i"
              class="howto__dot"
              :class="{ 'howto__dot--on': i === index }"
            />
          </span>
          <button v-if="index < slides.length - 1" class="primary howto__next" @click="index += 1">
            Next
          </button>
          <button v-else class="primary howto__next" @click="close">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { cardById, CARD_KINDS } from '@/engine/deck';

// The walkthrough. Matt: friends he invites have often never played, and they
// should be able to "get a sense of the lay of the land when they first
// arrive" without waiting for him to teach it.
//
// Slides, not a rules document - each one is a single idea with the game's own
// card art doing most of the talking. The finer numbers (starting money by
// player count, the removed cards) are deliberately left out: this teaches
// enough to take your first turn, and the interface narrates the details as
// they come up (the pass button spells out its own cost every time).
//
// App.vue owns WHEN this shows: automatically on a first-ever visit, and from
// the header's ? button forever after.
const SLIDES = [
  {
    title: 'Location, location',
    cards: ['p1', 'p15', 'p30'],
    lines: [
      'Thirty houses, from a cardboard box (1) to a space station (30). Higher is better, always.',
      'The game has two halves: first everyone <strong>buys</strong> houses at auction, then everyone <strong>sells</strong> them for cheques.',
      'Whoever ends with the most money — cheques plus whatever cash they never spent — wins.',
    ],
  },
  {
    title: 'Buying houses',
    cards: ['p8', 'p20', 'p28'],
    lines: [
      'Each round, one house per player is dealt face up. On your turn, <strong>bid</strong> — beating the last bid — or <strong>pass</strong>.',
      'Pass and you take the <strong>cheapest</strong> house on the table, and get half your bid back.',
      'The last player still bidding pays their <strong>whole</strong> bid and takes the best house.',
    ],
  },
  {
    title: 'Money is score',
    lines: [
      'Every dollar you don\'t spend counts at <strong>full value</strong> at the end of the game.',
      'So a bid is never just "spending" — it\'s trading points for a house that had better earn them back.',
      'Sometimes the smartest move is passing early, taking the cheap house for free, and keeping your money.',
    ],
  },
  {
    title: 'Selling houses',
    cards: ['c15', 'c8', 'c0'],
    lines: [
      'In the second half, cheques are dealt face up — from $15,000 down to the dreaded $0.',
      'Everyone secretly picks one house from their hand, then all flip at once. <strong>Best house takes the best cheque</strong>, second best the next, and so on.',
      'The art of it: win the big cheques with the <strong>smallest</strong> house that can, and dump your junk on the worthless rounds.',
    ],
  },
  {
    title: 'Reading the table',
    lines: [
      'Everyone\'s <strong>bids</strong> and <strong>houses</strong> are public. Everyone\'s <strong>money</strong> and <strong>cheques</strong> are secret until the end.',
      'So count what you can see: who spent big early, who\'s hoarding, whose hand is full of junk.',
      'That\'s the whole game: buy low, sell high, and let somebody else overpay for the mansion. Good luck.',
    ],
  },
];

export default {
  name: 'HowToPlay',
  emits: ['close'],
  data() {
    return { index: 0, slides: SLIDES, base: process.env.BASE_URL || '/' };
  },
  computed: {
    slide() { return this.slides[this.index]; },
  },
  methods: {
    close() { this.$emit('close'); },
    altFor(id) {
      // Slides name art files (p7 / c15); for cheques that is the shared
      // per-value file, so resolve through a matching card id.
      const card = cardById(id) || cardById(`${id}-0`);
      if (!card) return '';
      return card.kind === CARD_KINDS.PROPERTY
        ? `House card ${card.value}`
        : `A cheque for $${(card.value * 1000).toLocaleString('en-US')}`;
    },
  },
};
</script>
